import { useState, useEffect, useCallback } from "react";
import {
  ChefHat, Clock, CheckCheck, RefreshCw, Bell,
  Flame, CircleDot, PackageCheck, AlertTriangle,
  X, User, Phone, UtensilsCrossed, Package, Bike,
  FileText, CreditCard, Tag, Receipt, MessageSquare,
  ClipboardList,
} from "lucide-react";
import { useAdmin } from "../admin-context";

// ── Elapsed time helper ───────────────────────────────────────────────────────

const useNow = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(id);
  }, []);
  return now;
};

const elapsed = (dateStr, now) => {
  if (!dateStr) return null;
  const diff = Math.floor((now - new Date(dateStr).getTime()) / 1000);
  if (diff < 60)  return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
};

// urgency: 0-10m = green, 10-20m = yellow, 20m+ = red
const urgency = (dateStr, now) => {
  if (!dateStr) return "low";
  const mins = (now - new Date(dateStr).getTime()) / 60000;
  if (mins > 20) return "high";
  if (mins > 10) return "med";
  return "low";
};

const URGENCY_RING = {
  low:  "ring-gray-100",
  med:  "ring-yellow-300",
  high: "ring-red-400",
};

const URGENCY_TIME = {
  low:  "text-gray-400",
  med:  "text-yellow-500",
  high: "text-red-500 font-bold animate-pulse",
};

// ── Column config ─────────────────────────────────────────────────────────────

const COLUMNS = [
  {
    id:         "pending",
    label:      "New Orders",
    icon:       Bell,
    color:      "text-blue-600",
    bg:         "bg-blue-50",
    border:     "border-blue-200",
    headerBg:   "bg-blue-600",
    actionLabel:"Start Cooking",
    actionStyle:"bg-blue-500 hover:bg-blue-600 text-white",
    nextStatus: "confirmed",
  },
  {
    id:         "confirmed",
    label:      "In Progress",
    icon:       Flame,
    color:      "text-orange-600",
    bg:         "bg-orange-50",
    border:     "border-orange-200",
    headerBg:   "bg-orange-500",
    actionLabel:"Mark Ready",
    actionStyle:"bg-orange-500 hover:bg-orange-600 text-white",
    nextStatus: "shipped",
  },
  {
    id:         "shipped",
    label:      "Ready",
    icon:       PackageCheck,
    color:      "text-green-600",
    bg:         "bg-green-50",
    border:     "border-green-200",
    headerBg:   "bg-green-600",
    actionLabel:"Complete",
    actionStyle:"bg-green-500 hover:bg-green-600 text-white",
    nextStatus: "delivered",
  },
];

// ── Order Detail Modal ────────────────────────────────────────────────────────

const ORDER_TYPE_ICONS = {
  "dine-in":  UtensilsCrossed,
  "takeaway": Package,
  "delivery": Bike,
};

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

const OrderDetailModal = ({ order, col, onClose, onAdvance, advancing }) => {
  const items      = order.items || [];
  const TypeIcon   = ORDER_TYPE_ICONS[order.order_type] || ClipboardList;
  const subtotal   = items.reduce((s, i) => s + Number(i.price || 0) * Number(i.quantity || 1), 0);
  const discount   = Number(order.total_discount || 0);
  const tip        = Number(order.tip || 0);
  const total      = Number(order.total || order.total_price || subtotal - discount + tip);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden max-h-[90vh]">

          {/* Header */}
          <div className={`${col.headerBg} px-5 py-4 flex items-center justify-between flex-shrink-0`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                <ClipboardList size={18} className="text-white" />
              </div>
              <div>
                <p className="text-white font-extrabold text-base tracking-wide">Order #{order.id}</p>
                <p className="text-white/70 text-[11px]">{order.date ? new Date(order.date).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</p>
              </div>
            </div>
            <button onClick={onClose} type="button"
              className="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition">
              <X size={16} />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto flex-1 p-5 space-y-4">

            {/* Meta chips */}
            <div className="flex flex-wrap gap-2">
              {order.order_type && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full">
                  <TypeIcon size={12} /> {order.order_type}
                </span>
              )}
              {order.table_no && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full">
                  Table #{order.table_no}
                </span>
              )}
              {order.payment_method && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-purple-50 text-purple-700 px-3 py-1.5 rounded-full">
                  <CreditCard size={11} /> {order.payment_method}
                </span>
              )}
            </div>

            {/* Customer info */}
            {(order.customer_name || order.customer_phone) && (
              <div className="bg-gray-50 rounded-2xl px-4 py-3 space-y-1.5">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Customer</p>
                {order.customer_name && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <User size={13} className="text-gray-400 shrink-0" />
                    <span className="font-medium">{order.customer_name}</span>
                  </div>
                )}
                {order.customer_phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Phone size={13} className="text-gray-400 shrink-0" />
                    <span>{order.customer_phone}</span>
                  </div>
                )}
              </div>
            )}

            {/* Items */}
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Items</p>
              <div className="space-y-2">
                {items.length === 0 ? (
                  <p className="text-xs text-gray-300 italic px-1">No items</p>
                ) : (
                  items.map((item, i) => {
                    const name  = item.item?.name || item.name || `Item #${item.menu_item_id || i + 1}`;
                    const price = Number(item.price || 0);
                    const qty   = Number(item.quantity || 1);
                    return (
                      <div key={i} className="bg-gray-50 rounded-xl px-3 py-2.5">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-white border border-gray-200 text-gray-700 text-[11px] font-extrabold flex items-center justify-center flex-shrink-0 shadow-sm">
                            {qty}
                          </span>
                          <span className="flex-1 text-sm font-semibold text-gray-800">{name}</span>
                          {price > 0 && (
                            <span className="text-xs font-bold text-gray-500 shrink-0">{fmt(price * qty)}</span>
                          )}
                        </div>
                        {item.note && (
                          <div className="flex items-start gap-1.5 mt-1.5 ml-9">
                            <MessageSquare size={11} className="text-orange-400 mt-0.5 shrink-0" />
                            <p className="text-[11px] text-orange-600 italic">{item.note}</p>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Order note */}
            {order.order_note && (
              <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-start gap-2">
                <FileText size={14} className="text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] font-bold text-amber-500 uppercase tracking-wide mb-0.5">Order Note</p>
                  <p className="text-sm text-amber-800">{order.order_note}</p>
                </div>
              </div>
            )}

            {/* Totals */}
            {subtotal > 0 && (
              <div className="bg-gray-50 rounded-2xl px-4 py-3 space-y-1.5">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Summary</p>
                <div className="flex justify-between text-xs text-gray-500"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
                {discount > 0 && <div className="flex justify-between text-xs text-green-600"><span className="flex items-center gap-1"><Tag size={10} /> Discount</span><span>−{fmt(discount)}</span></div>}
                {tip > 0 && <div className="flex justify-between text-xs text-blue-600"><span>Tip</span><span>+{fmt(tip)}</span></div>}
                <div className="flex justify-between font-extrabold text-sm text-gray-900 pt-1.5 border-t border-gray-200">
                  <span className="flex items-center gap-1.5"><Receipt size={13} className="text-orange-400" /> Total</span>
                  <span className="text-orange-600">{fmt(total)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Footer action */}
          <div className="px-5 pb-5 pt-3 flex-shrink-0 border-t border-gray-100">
            <button
              onClick={() => { onAdvance(order.id, col.nextStatus); onClose(); }}
              disabled={advancing === order.id}
              className={`w-full py-3 rounded-2xl text-sm font-bold transition shadow-sm
                ${advancing === order.id ? "opacity-40 cursor-not-allowed bg-gray-100 text-gray-400" : col.actionStyle}`}
            >
              {advancing === order.id ? "Updating…" : col.actionLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// ── Order Card ────────────────────────────────────────────────────────────────

const OrderCard = ({ order, col, now, onAdvance, advancing, onDetails }) => {
  const time    = elapsed(order.date, now);
  const urg     = urgency(order.date, now);
  const items   = order.items || [];

  return (
    <div
      className={`bg-white rounded-2xl border-2 shadow-sm flex flex-col overflow-hidden transition-all
        ring-2 ${URGENCY_RING[urg]} ${col.border}`}
    >
      {/* Card header */}
      <div className={`${col.headerBg} px-4 py-2.5 flex items-center justify-between`}>
        <span className="text-white font-extrabold text-sm tracking-wide">#{order.id}</span>
        <div className="flex items-center gap-1.5">
          {urg === "high" && <AlertTriangle size={13} className="text-yellow-300" />}
          <Clock size={12} className="text-white/70" />
          <span className={`text-xs text-white/90 ${urg === "high" ? "font-bold" : ""}`}>{time}</span>
        </div>
      </div>

      {/* Items */}
      <div className="px-4 py-3 flex-1 space-y-1.5">
        {items.length === 0 ? (
          <p className="text-xs text-gray-300 italic">No items</p>
        ) : (
          items.map((item, i) => {
            const note = item.note;
            return (
              <div key={i} className="flex items-start gap-2">
                <span className="mt-0.5 w-5 h-5 flex-shrink-0 rounded-full bg-gray-100 text-gray-600 text-[10px] font-bold flex items-center justify-center">
                  {item.quantity}
                </span>
                <div className="min-w-0">
                  <span className="text-sm text-gray-800 font-medium leading-tight block">
                    {item.item?.name || item.name || `Item #${item.menu_item_id || i + 1}`}
                  </span>
                  {note && (
                    <span className="text-[11px] text-orange-500 italic leading-tight block">↳ {note}</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="px-3 pb-3 flex flex-col gap-2">
        {(order.customer_name || order.table_no || order.order_type) && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {order.order_type && (() => { const Icon = ORDER_TYPE_ICONS[order.order_type]; return Icon ? <Icon size={11} className="text-gray-400" /> : null; })()}
            {order.table_no && <span className="text-[11px] text-gray-400">Table #{order.table_no}</span>}
            {order.customer_name && <span className="text-[11px] text-gray-400 truncate">· {order.customer_name}</span>}
          </div>
        )}
        {order.order_note && (
          <div className="flex items-center gap-1 text-[10px] text-amber-600 bg-amber-50 rounded-lg px-2 py-1">
            <FileText size={10} className="shrink-0" /> <span className="truncate">{order.order_note}</span>
          </div>
        )}
        <div className="flex gap-2">
          <button
            onClick={() => onDetails(order)}
            type="button"
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-gray-200 text-gray-600 bg-gray-50 hover:bg-gray-100 hover:border-gray-300 transition"
          >
            <ClipboardList size={12} /> Details
          </button>
          <button
            onClick={() => onAdvance(order.id, col.nextStatus)}
            disabled={advancing === order.id}
            type="button"
            className={`flex-1 py-2 rounded-xl text-xs font-bold transition
              ${advancing === order.id ? "opacity-40 cursor-not-allowed bg-gray-100 text-gray-400" : col.actionStyle}`}
          >
            {advancing === order.id ? "Updating…" : col.actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Column ────────────────────────────────────────────────────────────────────

const KitchenColumn = ({ col, orders, now, onAdvance, advancing, onDetails }) => {
  const Icon = col.icon;
  return (
    <div className="flex flex-col gap-3 min-w-0">
      {/* Column header */}
      <div className={`flex items-center gap-2 px-4 py-3 rounded-2xl ${col.bg} border ${col.border}`}>
        <Icon size={18} className={col.color} />
        <span className={`font-bold text-base ${col.color}`}>{col.label}</span>
        <span className={`ml-auto text-xs font-extrabold px-2.5 py-0.5 rounded-full ${col.bg} border ${col.border} ${col.color}`}>
          {orders.length}
        </span>
      </div>

      {/* Cards */}
      <div className="space-y-3 overflow-y-auto flex-1 min-h-0 pr-0.5 scrollbar-thin">
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 rounded-2xl border-2 border-dashed border-gray-100 text-gray-200 gap-2">
            <CircleDot size={22} />
            <p className="text-xs">No orders</p>
          </div>
        ) : (
          orders.map(order => (
            <OrderCard
              key={order.id}
              order={order}
              col={col}
              now={now}
              onAdvance={onAdvance}
              advancing={advancing}
              onDetails={onDetails}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ── Main Kitchen Display Page ─────────────────────────────────────────────────

const KitchenDisplayPage = () => {
  const { orders, loadOrders, updateOrderStatus, showToast } = useAdmin();
  const now = useNow();
  const [advancing,     setAdvancing]     = useState(null);
  const [refreshing,    setRefreshing]    = useState(false);
  const [showDone,      setShowDone]      = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const selectedCol = selectedOrder
    ? COLUMNS.find(c => c.id === selectedOrder.status) || COLUMNS[2]
    : null;

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const id = setInterval(() => loadOrders(), 30_000);
    return () => clearInterval(id);
  }, [loadOrders]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadOrders();
    setRefreshing(false);
  }, [loadOrders]);

  const handleAdvance = useCallback(async (id, nextStatus) => {
    setAdvancing(id);
    try {
      await updateOrderStatus(id, nextStatus);
      if (nextStatus === "delivered") {
        showToast(`Order #${id} completed`, "success");
      }
    } catch {
      showToast("Failed to update order", "error");
    } finally {
      setAdvancing(null);
    }
  }, [updateOrderStatus, showToast]);

  // Only today's orders are relevant for kitchen
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayOrders = orders.filter(o => !o.date || new Date(o.date) >= todayStart);

  // Split by column status
  const byStatus = (status) =>
    todayOrders
      .filter(o => o.status === status)
      .sort((a, b) => new Date(a.date) - new Date(b.date)); // oldest first

  const doneToday = todayOrders.filter(o => o.status === "delivered")
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const totalActive = ["pending", "confirmed", "shipped"]
    .reduce((s, st) => s + byStatus(st).length, 0);

  return (
    <div className="flex flex-col gap-5 h-[calc(100vh-8rem)] min-h-0">

      {/* Order detail modal */}
      {selectedOrder && selectedCol && (
        <OrderDetailModal
          order={selectedOrder}
          col={selectedCol}
          onClose={() => setSelectedOrder(null)}
          onAdvance={handleAdvance}
          advancing={advancing}
        />
      )}

      {/* Top bar */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
            <ChefHat size={20} className="text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kitchen Display</h1>
            <p className="text-xs text-gray-400">
              {totalActive} active order{totalActive !== 1 ? "s" : ""} · auto-refreshes every 30s
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDone(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition
              ${showDone ? "bg-green-500 text-white border-green-500" : "bg-white border-gray-200 text-gray-600 hover:border-green-300 hover:text-green-600"}`}
          >
            <CheckCheck size={14} />
            Done today ({doneToday.length})
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-600 hover:border-orange-300 hover:text-orange-600 transition disabled:opacity-40"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {showDone ? (
        /* ── Completed today panel ── */
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {doneToday.length === 0 ? (
              <div className="col-span-full flex flex-col items-center justify-center h-40 text-gray-200 gap-2">
                <CheckCheck size={32} />
                <p className="text-sm">No completed orders yet today</p>
              </div>
            ) : (
              doneToday.map(order => (
                <div key={order.id} className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden opacity-70">
                  <div className="bg-green-600 px-4 py-2.5 flex items-center justify-between">
                    <span className="text-white font-extrabold text-sm">#{order.id}</span>
                    <CheckCheck size={14} className="text-white/80" />
                  </div>
                  <div className="px-4 py-3 space-y-1">
                    {(order.items || []).map((item, i) => (
                      <div key={i} className="flex gap-2 text-xs text-gray-500">
                        <span className="font-bold text-gray-600">{item.quantity}×</span>
                        <span>{item.item?.name || item.name || `Item #${item.menu_item_id || i + 1}`}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        /* ── Three-column Kanban ── */
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-3 gap-4">
          {COLUMNS.map(col => (
            <KitchenColumn
              key={col.id}
              col={col}
              orders={byStatus(col.id)}
              now={now}
              onAdvance={handleAdvance}
              advancing={advancing}
              onDetails={setSelectedOrder}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default KitchenDisplayPage;
